import React, { useState } from 'react';
import { useCookies } from 'react-cookie';
import { MdOutlineKeyboardReturn } from 'react-icons/md';
import { useIntl } from 'react-intl';
import styled from 'styled-components/macro';

import { borderStyle, uiColors } from '../../common/styles';
import FramiButton from '../common/FramiButton';
import { CompositionRegularIcon, FrameFilledIcon, PhotosAlbumIcon } from '../../icons';
import { useAppContext, useComposition, useFrami, useModal, useNavigation, useWorkspace } from '../../context';
import {
    FRAMESIZE_DRAWER,
    FRAMESTYLE_DRAWER,
    IMAGE_DRAWER,
    TEMPLATE_DRAWER,
    WALLSIZE_DRAWER,
} from '../../providers/NavigationProvider';
import ToolbarAnimatedHint from './ToolbarAnimatedHint';

const Wrapper = styled.span`
    position: relative;
    text-align: center;
    // width: ${(props) => (!props.$isSmall ? `75px` : 'unset')};
    align-content: center;
`;

const ToolButton = styled(FramiButton)`
    margin: ${(props) => (!props.$isSmall ? 'auto' : '2px 6px')};
    color: ${(props) => (props.selected ? uiColors.GRAY_800 : uiColors.SECONDARY_TEXT)};
    border: ${(props) => (props.selected ? borderStyle.outline : borderStyle.outlineTransparent)};
    border-radius: 12px;
    background: ${(props) => (props.selected ? uiColors.PRIMARY_0 : 'transparent')};

    &:disabled {
        opacity: ${(props) => (props.selected ? 1 : 0.3)};
    }

    @keyframes spin {
        from {
            transform: rotate(0deg);
            color: ${uiColors.PRIMARY_500};
        }
        to {
            transform: rotate(360deg);
            color: ${uiColors.PRIMARY_500};
        }
    }
    padding: 6px 8px;

    animation: ${(props) => (props.$spin ? 'spin 2s linear infinite' : 'unset')};
`;

// const Description = styled.p`
//     font-size: 12px;
//     font-weight: 400;
//     color: ${(props) => (props.$selected ? uiColors.LINK_TEXT : uiColors.SECONDARY_TEXT)};
//     opacity: ${(props) => (props.$disabled ? 0.3 : 'unset')};
//     margin-bottom: ${(props) => (props.$isMobile ? '6px' : 'unset')};
//     cursor: ${(props) => (props.$disabled ? 'default' : 'pointer')};
// `;

const ToolbarOptions = () => {
    const { isMobile, smallHeightDevice, isHandheldDevice } = useAppContext();
    const { isFramiObsolete } = useFrami();
    const {
        currentComp,
        activeFrame,
        setSelectedFrame,
        goBackToLastNonVoucherComposition,
    } = useComposition();
    const { activeDrawer, activeHint, openDrawer } = useNavigation();
    const { isEditMode, setFrameOrbitControls, closeEditMode, saveEditedComposition } = useWorkspace();
    const { openModal } = useModal();

    // const [isLoading, setIsLoading] = useState();
    const [showSelection, setShowSelection] = useState(false);
    const [cookies, setCookie] = useCookies(['templateDrawerClickHintSeen']);

    const intl = useIntl();

    // const handleOpenModelViewer = async () => {
    //     setIsLoading(true);
    //     if (currentComp?.lut_filter && currentComp?.lut_filter !== 'NONE') {
    //         prepareFilteredImages();
    //         setOpenModelViewer(true);
    //     } else {
    //         setIsExportingGLTF(true);
    //     }
    // };

    // const onVoucherCreate = () => {
    //     getOrCreateVoucher();
    //     if (isMobile) {
    //         openDrawer(VOUCHER, isMobile);
    //     }
    // };

    const disabledActiveFrameDrawer = [WALLSIZE_DRAWER, TEMPLATE_DRAWER];

    const mainOptions = [
        ...(isFramiObsolete
            ? []
            : [
                {
                    id: TEMPLATE_DRAWER,
                    icon: <CompositionRegularIcon />,
                    onClick: () => {
                        if (currentComp?.is_voucher) {
                            goBackToLastNonVoucherComposition();
                        }
                        openDrawer(TEMPLATE_DRAWER, true);
                        if (!cookies.templateDrawerClickHintSeen) {
                            setCookie('templateDrawerClickHintSeen', true, { maxAge: 120960000, path: '/' });
                        }
                    },
                    disabled: currentComp?.baseline_data || currentComp?.is_ordered,
                    description: 'studio.tools.layouts',
                    activeHint: 1,
                    selected: showSelection || !activeDrawer,
                },
            ]
        ),
    ];
    mainOptions.push(
        {
            id: IMAGE_DRAWER,
            icon: <PhotosAlbumIcon />,
            onClick: () => openDrawer(IMAGE_DRAWER, true),
            disabled:
                currentComp?.baseline_data && !currentComp?.is_ordered
                    ? false
                    : currentComp?.is_ordered ||
                      !currentComp ||
                      (currentComp?.is_voucher &&
                          ['NF', 'DIGITAL'].includes(currentComp?.frames_json[0]?.frame_material)),
            description: currentComp?.is_voucher ? 'studio.tools.photo' : 'studio.tools.photos',
        },
        {
            id: FRAMESTYLE_DRAWER,
            icon: <FrameFilledIcon />,
            onClick: () => openDrawer(FRAMESTYLE_DRAWER, true),
            disabled:
                currentComp?.baseline_data ||
                currentComp?.is_ordered ||
                !currentComp ||
                (currentComp?.is_voucher && ['NF', 'DIGITAL'].includes(currentComp?.frames_json[0]?.frame_material)),
            description: activeFrame || currentComp?.is_voucher ? 'studio.tools.frame' : 'studio.tools.frames',
        },
    )

        // {
        //     id: 'Hanging System',
        //     icon: <PiHandsClapping />,
        //     onClick: () => openModal('hangingSystem'),
        //     description: 'studio.tools.hangingSystem',
        //     selected: modal?.type === 'hangingSystem',
        // },
        // {
        //     id: '3D',
        //     icon: isLoading ? <MdSync /> : <Md3DRotation />,
        //     onClick: () => {
        //         if (activeFrame) {
        //             setFrameOrbitControls(!frameOrbitControls);
        //         } else {
        //             handleOpenModelViewer();
        //         }
        //     },
        //     disabled: !currentComp || !compositionModelLoaded || currentComp?.is_voucher,
        //     description: 'studio.tools.3d',
        //     selected: frameOrbitControls,
        // },
        // {
        //     id: VOUCHER,
        //     icon: <MdCardGiftcard />,
        //     onClick: onVoucherCreate,
        //     description: 'studio.tools.voucher',
        // },
        // {
        //     id: FAVOURITE_DRAWER,
        //     icon: (
        //         <FramiBadge
        //             badgeStyle={{
        //                 right: '4px',
        //                 top: '18px',
        //                 backgroundColor:
        //                     activeDrawer === FAVOURITE_DRAWER ? uiColors.GRAY_800 : uiColors.SECONDARY_TEXT,
        //             }}
        //             onClick={() => openDrawer(FAVOURITE_DRAWER)}
        //             badgeContent={parsedComps?.favouriteComps?.length}
        //         >
        //             <MdOutlineFavoriteBorder size={24} />
        //         </FramiBadge>
        //     ),
        //     onClick: () => openDrawer(FAVOURITE_DRAWER, !isMobile),
        //     disabled: !currentComp,
        //     description: 'studio.tools.favourite',
        // },

    let options = mainOptions

    if (isEditMode) {
        options = [
            ...(smallHeightDevice
                ? [
                      {
                          id: 'BACK',
                          icon: <MdOutlineKeyboardReturn />,
                          onClick: () => {
                              currentComp?.frames_json.length > 0
                                  ? openModal('closeEdit', { onClose: closeEditMode, onSubmit: saveEditedComposition })
                                  : closeEditMode();
                          },
                          description: 'studio.tools.back',
                      },
                  ]
                : []),
            {
                id: FRAMESIZE_DRAWER,
                icon: <FrameFilledIcon />,
                onClick: () => openDrawer(FRAMESIZE_DRAWER, !isMobile),
                // disabled: currentComp?.is_ordered || !currentComp,
                description: 'studio.tools.editMode.addFrame',
            },
            ...options.filter((o) => [WALLSIZE_DRAWER].includes(o.id)),
        ];
    } else if (activeFrame && !currentComp?.is_voucher) {
        options = [
            {
                id: 'BACK',
                icon: <MdOutlineKeyboardReturn />,
                onClick: () => {
                    setSelectedFrame(null);
                    setFrameOrbitControls(false);
                },
                description: 'studio.tools.back',
            },
            ...options.filter((o) => !disabledActiveFrameDrawer.includes(o.id)),
        ];
    }

    return options.map((o, index) => (
        <Wrapper $isSmall={smallHeightDevice} key={`toolbar-${index}`}>
            <ToolButton
                className={`toolButton-${index}`}
                dataPh={`${isEditMode ? 'edit ' : ''}toolbar button ${o.id}`}
                $isMobile={isMobile}
                title={intl.formatMessage({ id: o.description })}
                dataCy={`toolbar_button-${o.id}`}
                disabled={o.disabled || o.id === activeDrawer}
                selected={o.selected || o.id === activeDrawer}
                size={isHandheldDevice ? 40 : 46}
                leftIcon={o.icon}
                onClick={o.onClick}
                // $spin={isLoading && o.id === '3D'}
                $isSmall={smallHeightDevice}
                iconShowMinWidth={0}
            />
            {isMobile && o.activeHint === activeHint && <ToolbarAnimatedHint select={setShowSelection} />}
            {/* {!smallHeightDevice && window.innerWidth >= 320 && (
                <Description
                    $isMobile={isMobile}
                    $selected={o.selected || o.id === activeDrawer}
                    $disabled={o.disabled}
                    {...(!o.disabled && o.id !== activeDrawer && { onClick: o.onClick })}
                >
                    {intl.formatMessage({ id: o.description })}
                </Description>
            )} */}
        </Wrapper>
    ));
};

export default ToolbarOptions;
